import React, { useState, useEffect, useRef, useCallback } from 'react';
import DataGrid, { Column, Scrolling, Sorting } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { useSignalR } from '../../../hooks/useSignalR';
import { signalRConfig } from '../../../config/signalr-config';
import './PublicCompetitionScoreboard.css';

// Komponente für Splittimes-Zelle mit automatischem Scroll
const SplitTimesCell = ({ splitTimes }) => {
    const scrollDivRef = useRef(null);
    
    useEffect(() => {
        if (scrollDivRef.current) {
            // Kleiner Delay um sicherzustellen, dass das DOM vollständig ist
            setTimeout(() => {
                if (scrollDivRef.current) {
                    scrollDivRef.current.scrollLeft = scrollDivRef.current.scrollWidth;
                }
            }, 0);
        }
    }, [splitTimes]);
    
    if (!splitTimes || splitTimes.length === 0) {
        return <span></span>;
    }
    
    return (
        <div 
            ref={scrollDivRef}
            style={{
                display: 'flex',
                overflowX: 'auto',
                gap: '8px',
                padding: '0px',
                whiteSpace: 'nowrap',
                scrollBehavior: 'smooth'
            }}
            className="hide-scrollbar"
        >
            {splitTimes.map((time, index) => {
                // Bestimme die Farbe basierend auf der Platzierung in Klammern
              let backgroundColor = 'transparent';
                if (time?.includes('(1.)')) {
                    backgroundColor = '#FFD700'; // Gold
                } else if (time?.includes('(2.)')) {
                    backgroundColor = '#C0C0C0'; // Silber
                } else if (time?.includes('(3.)')) {
                    backgroundColor = '#CD7F32'; // Bronze
                }
                
                return (
                    <span 
                        key={index}
                        style={{
                            display: 'inline-block',
                            minWidth: '60px',
                            padding: '2px 6px',
                            backgroundColor: backgroundColor,
                            borderRadius: '3px',
                            fontSize: '0.85em',
                            color: backgroundColor === '#FFD700' || backgroundColor === '#C0C0C0' ? '#000' : 'inherit'
                        }}
                    >
                        {time}
                    </span>
                );
            })}
        </div>
    );
};

// Status-Mapping für Heat-Status
const HeatStatusMap = {
    0: 'Nicht gestartet',
    1: 'Läuft',
    2: 'Beendet',
    3: 'Offiziell'
};

// Konvertiert competitors-Objekt zu Array für DataGrid
const competitorsToArray = (competitors) => {
    if (!competitors) return [];
    
    return Object.entries(competitors).map(([laneNumber, competitor]) => ({
        lane: laneNumber, // String als Key behalten für Konsistenz mit Updates
        firstName: competitor.firstName || '',
        lastName: competitor.lastName || '',
        code: competitor.code || '',
        clubName: competitor.clubName || '',
        nation: competitor.nation || '',
        gender: competitor.gender || '',
         birthday: competitor.birthday || '',
         birthYear: competitor.birthYear || '',
        // Zeiten werden später per Update hinzugefügt
        time: competitor.time || null,
        splitTimes: competitor.splitTimes || [],
        lastSplitTime: competitor.lastSplitTime || null,
        rank: competitor.rank || null,
        status: competitor.status || null
    })).sort((a, b) => parseInt(a.lane) - parseInt(b.lane));
};

// Header-Komponente für Event/Heat Info
const ScoreboardHeader = ({ session, event, heat, status, lastStart }) => {
    const [elapsedText, setElapsedText] = useState('');

    const formatElapsed = (start) => {
        if (!start) return '';
        const startTime = typeof start === 'number' ? new Date(start) : new Date(start);
        const now = new Date();
        const ms = now - startTime;
        if (ms < 0) return '';
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const centiseconds = Math.floor((ms % 1000) / 10);
        const hh = hours > 0 ? String(hours).padStart(2, '0') + ':' : '';
        const mm = String(minutes).padStart(2, '0');
        const ss = String(seconds).padStart(2, '0');
        const cs = String(centiseconds).padStart(2, '0');
        return `${hh}${mm}:${ss}.${cs}`;
    };

    useEffect(() => {
        if (status === 1 && lastStart) {
            // sofort initial setzen
            setElapsedText(formatElapsed(lastStart));
            const interval = setInterval(() => {
                setElapsedText(formatElapsed(lastStart));
            }, 100);
            return () => clearInterval(interval);
        } else {
            setElapsedText('');
        }
    }, [status, lastStart]);

    return (
        <div style={{
            backgroundColor: '#f5f5f5',
            padding: '10px',
            borderRadius: '5px 5px 0 0',
            borderBottom: '2px solid #ddd',
            boxSizing: 'border-box',
            width: '100%'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <h3 style={{ margin: 0, fontSize: 'clamp(1rem, 3vw, 1.17rem)' }}>
                      {event && heat ? `${event.eventDiscipline} - Lauf ${heat.heatNumber}` : '—'}
                    </h3>
                    <span style={{ color: '#666', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                        {event && heat ? (
                            <>
                                {session && ` Abschnitt ${session.sessionNumber}`} | WK {event.eventName} - {event.eventDiscipline} | Lauf {heat.heatNumber}
                            </>
                        ) : '—'}
                    </span>
                </div>
                {status !== -1 && (
                    <div style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        backgroundColor: status === 1 ? '#4CAF50' : status === 2 ? '#2196F3' : '#9E9E9E',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: '100px',
                        textAlign: 'center'
                    }}>
                        {status === 1 && lastStart ? elapsedText : (HeatStatusMap[status] || 'Unbekannt')}
                    </div>
                )}
            </div>
        </div>
    );
};

export function PublicCompetitionScoreboard({ competitionName }) {
    const [scoreboardData, setScoreboardData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState(null);
    const [gridKey, setGridKey] = useState(0); // Key um Grid bei neuem Lauf neu zu laden

    // Ref für den CustomStore, um push() aufrufen zu können
    const storeRef = useRef(null);
    // Ref für die aktuellen Competitors-Daten (für Store load)
    const competitorsRef = useRef([]);

    // SignalR Hook für Scoreboard
    const scoreboardHubUrl = signalRConfig.hubUrls.scoreboard;

    const {
        isConnected,
        connectionState,
        invokeMethod,
        onMessage,
        error: signalRError
    } = useSignalR(scoreboardHubUrl);

    // Funktion um CustomStore mit neuen Daten zu erstellen
    const createStoreWithData = useCallback((competitors) => {
        const store = new CustomStore({
            key: 'lane',
            load: () => {
                return Promise.resolve(competitors);
            }
        });
        storeRef.current = store;
        return store;
    }, []);

    // Funktion um alle Scoreboard-Daten zu aktualisieren (Header + DataGrid)
    const updateScoreboardData = useCallback((data) => {
        setScoreboardData(data);
        
        // Competitors-Daten für den Store aktualisieren
        if (data?.competitors) {
            const competitorsArray = competitorsToArray(data.competitors);
            competitorsRef.current = competitorsArray;
            
            // Store nur einmal initial erstellen
            if (!storeRef.current) {
                const newStore = createStoreWithData(competitorsArray);
                setDataSource(newStore);
              //  setGridKey(prev => prev + 1);
            } else {
                // Bestehenden Store mit neuen Daten aktualisieren
                const updates = competitorsArray.map(competitor => ({
                    type: 'update',
                    key: competitor.lane,
                    data: competitor
                }));
                storeRef.current.push(updates);
            }
        }
    }, [createStoreWithData]);

    // Funktion um nur den Status im Header zu aktualisieren
    const updateStatus = useCallback((data) => {
        setScoreboardData(prev => prev ? { ...prev, status: data.status, lastStart: data.lastStart } : prev);
    }, []);

    // Funktion um Endzeit für eine Lane zu aktualisieren
    // Format: {"lane":"2","time":"06,70","rank":"1","status":"finished"}
    const updateLaneTimes = useCallback((data) => {
        if (!data || !data.lane) {
            console.warn('Invalid lane time data received:', data);
            return;
        }

        const lane = data.lane; // String
        
        // Lokale Daten aktualisieren
        const laneIndex = competitorsRef.current.findIndex(c => c.lane === lane);
        if (laneIndex !== -1) {
            competitorsRef.current[laneIndex] = {
                ...competitorsRef.current[laneIndex],
                time: data.time,
                rank: data.rank,
                status: data.status
            };

            // Store mit push() aktualisieren - nur diese eine Zeile wird neu gerendert!
            if (storeRef.current) {
                storeRef.current.push([{
                    type: 'update',
                    key: lane,
                    data: competitorsRef.current[laneIndex]
                }]);
            }
        }
    }, []);

    // Funktion um Split-Zeiten für eine Lane zu aktualisieren
    // Format: {"lane":"2","splitTimes":["03,96(1.)"]}
    const updateLaneSplitTimes = useCallback((data) => {
        if (!data || !data.lane) {
            console.warn('Invalid lane split data received:', data);
            return;
        }

        const lane = data.lane; // String
        
        // Lokale Daten aktualisieren
        const laneIndex = competitorsRef.current.findIndex(c => c.lane === lane);
        if (laneIndex !== -1) {
            const newSplits = data.splitTimes || [];
            competitorsRef.current[laneIndex] = {
                ...competitorsRef.current[laneIndex],
                splitTimes: newSplits,
                lastSplitTime: newSplits.length > 0 ? newSplits[newSplits.length - 1] : competitorsRef.current[laneIndex].lastSplitTime
            };

            // Store mit push() aktualisieren - nur diese eine Zeile wird neu gerendert!
            if (storeRef.current) {
                storeRef.current.push([{
                    type: 'update',
                    key: lane,
                    data: competitorsRef.current[laneIndex]
                }]);
            }
        }
    }, []);

    // Effect für Scoreboard-spezifische Logik
    useEffect(() => {
        if (isConnected && competitionName) {
            // GetScoreboard mit competitionName abrufen
            const fetchScoreboard = async () => {
                try {
                    setLoading(true);
                    setError(null);

                    const result = await invokeMethod('GetScoreboard', competitionName);
                      // Initiale Daten setzen
                    updateScoreboardData(JSON.parse(result));
                } catch (err) {
                    console.error('Error fetching scoreboard:', err);
                    setError(err.message || 'Fehler beim Laden des Scoreboards');
                } finally {
                    setLoading(false);
                }
            };

            fetchScoreboard();

            // Scoreboard Updates abonnieren (komplettes Update von Header + DataGrid)
            onMessage('ScoreboardUpdate', (data) => {
                // Bei neuem Lauf: Store zurücksetzen und Grid neu initialisieren
                storeRef.current = null;
                competitorsRef.current = [];
                updateScoreboardData(data);
            });

            // Scoreboard Times Updates abonnieren
            // Format: {"lane":"2","time":"06,70","rank":"1","status":"finished"}
            onMessage('ScoreboardUpdateTimes', (data) => {
                updateLaneTimes(data);
            });

            // Scoreboard Split Times Updates abonnieren
            // Format: {"lane":"2","splitTimes":["03,96(1.)"]}
            onMessage('ScoreboardUpdateSplitTimes', (data) => {
                updateLaneSplitTimes(data);
            });

            // Scoreboard Status Updates abonnieren (nur Status im Header)
            onMessage('ScoreboardUpdateStatus', (data) => {
                updateStatus(data);
            });
        }
    }, [isConnected, competitionName, invokeMethod, onMessage, updateScoreboardData, updateStatus, updateLaneTimes, updateLaneSplitTimes]);

    // Fehlerbehandlung für SignalR
    useEffect(() => {
        if (signalRError) {
            setError(signalRError);
        }
    }, [signalRError]);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>

                {loading && <span style={{ color: 'blue' }}>Lade Scoreboard...</span>}
            </div>

            {error && (
                <div style={{
                    color: 'red',
                    backgroundColor: '#ffe6e6',
                    padding: '10px',
                    borderRadius: '5px',
                    marginBottom: '10px'
                }}>
                    Fehler: {error}
                </div>
            )}

            {scoreboardData && dataSource && (
                <div style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '5px', 
                    width: '100%', 
                    maxWidth: '1000px',
                    margin: '0 auto',
                    overflowX: 'auto' 
                }}>
                    {/* Header mit Event/Heat Info und Status */}
                    <ScoreboardHeader 
                        session={scoreboardData.session}
                        event={scoreboardData.event} 
                        heat={scoreboardData.heat} 
                        status={scoreboardData.status}
                        lastStart={scoreboardData.lastStart}
                    />

                    {/* DataGrid für Competitors */}
                    <DataGrid
                        key={gridKey}
                        dataSource={dataSource}
                        showBorders={true}
                        repaintChangesOnly={true}
                        highlightChanges={true}
                        columnAutoWidth={true}

                    >
                        <Scrolling mode="virtual" />
                        <Sorting mode="single" />

                        <Column 
                            dataField="lane" 
                            caption="Bahn" 
                            width={60}
                            alignment="center"
                            fixed={true}
                            fixedPosition="left"
                        />
                    
                        <Column 
                            dataField="lastName" 
                            caption="Name"
                          
                            calculateCellValue={(rowData) => rowData.firstName + " " + rowData.lastName}
                            fixedPosition="left"
                            cellRender={(cellData) => (
                                <div style={{ 
                                    fontWeight: 'bold',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    lineHeight: '1.2'
                                }}>
                                    {cellData.data.firstName} {cellData.data.lastName}
                                </div>
                            )}
                        />
                        
                        <Column 
                            dataField="clubName" 
                            caption="Verein"
                         hidingPriority={0}
                            cellRender={(cellData) => (
                                <div style={{ 
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }} title={cellData.data.clubName}>
                                    {cellData.data.clubName}
                                </div>
                            )}
                        />
                         <Column 
                            dataField="birthYear" 
                            caption="JG"
                            width={60}
                            hidingPriority={1}
                        />
                        
                        <Column 
                            dataField="splitTimes" 
                            caption="Splits"
                           
                            minWidth={150}
                            alignment="center"
                            cellRender={(cellData) => (
                                <SplitTimesCell splitTimes={cellData.data.splitTimes} />
                            )}
                        />
                        <Column 
                            dataField="time" 
                            caption="Endzeit"
                            width={100}
                            alignment="center"
                            cellRender={(cellData) => {
                                const data = cellData.data;
                                if (data.status === 'dns') return <span style={{ color: 'red', fontWeight: 'bold' }}>DNS</span>;
                                if (data.status === 'dnf') return <span style={{ color: 'red', fontWeight: 'bold' }}>DNF</span>;
                                if (data.status === 'dsq') return <span style={{ color: 'red', fontWeight: 'bold' }}>DSQ</span>;
                                return <span style={{ fontWeight: 'bold' }}>{data.time}</span>;
                            }}
                        />
                          <Column 
                            dataField="rank" 
                            caption="Platz" 
                            width={60}
                            alignment="center"
                            cellRender={(cellData) => {
                                const rank = cellData.data.rank;
                                if (!rank) return <span></span>;
                                
                                let backgroundColor = 'transparent';
                                if (rank === '1') {
                                    backgroundColor = '#FFD700'; // Gold
                                } else if (rank === '2') {
                                    backgroundColor = '#C0C0C0'; // Silber
                                } else if (rank === '3') {
                                    backgroundColor = '#CD7F32'; // Bronze
                                }
                                
                                return (
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '2px 6px',
                                        backgroundColor: backgroundColor,
                                        borderRadius: '3px',
                                        color: backgroundColor !== 'transparent' ? '#000' : 'inherit'
                                    }}>
                                        {rank}.
                                    </span>
                                );
                            }}
                        />
                    </DataGrid>
                </div>
            )}
        </div>
    );
}
